import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Badge, Icon } from "@/design-system";
import { openCartDrawer } from "@/redux/slices/cartSlice";

/**
 * The header's cart button. A left-click opens the slide-in `CartDrawer`
 * (mounted once in `App.js`), exactly the reference's `openCart` trigger — the
 * drawer replaced the old hover preview panel, so there is no hover behaviour
 * here at all any more.
 *
 * It stays a `<Link to="/cart">` rather than the reference's `<button>`, same as
 * the nav menus' rows: middle-click and Cmd-click still open the full cart page
 * in a new tab, and only the plain click is intercepted for the drawer.
 */
export default function CartIndicator() {
  const cartItems = useSelector((state) => state.cart.items);
  const cartLoading = useSelector((state) => state.cart.loading);
  const dispatch = useDispatch();

  const itemCount = cartItems.reduce((total, item) => {
    const quantity = Number(item.quantity);
    return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
  }, 0);
  const hasItems = itemCount > 0;

  return (
    <Link
      to="/cart"
      aria-label={`Cart${hasItems ? `, ${itemCount} items` : ""}`}
      aria-haspopup="dialog"
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        dispatch(openCartDrawer());
      }}
      className="flex flex-col items-center gap-[2px] rounded-md px-[var(--space-3)] py-[6px] font-sans text-ink [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-soft hover:text-blue-700 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
    >
      <Badge count={cartLoading ? 0 : itemCount}>
        <Icon
          name="shopping_cart"
          size={26}
          weight={400}
          className={cartLoading ? "animate-pulse" : ""}
        />
      </Badge>
      <span className="text-xs font-semibold">Cart</span>
    </Link>
  );
}
